// src/modules/app-views/_apps/appmulti/plans/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { Card, Table, Tooltip, Button } from 'antd';
import { BiExpandAlt, BiCollapseAlt } from 'react-icons/bi';
import ActionsButton from 'layouts/Pages/PageHeader/ActionsButton';
import { useListContext } from 'components/core/ListProvider';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { planWithLogoColumn, operatorColumn, priceColumn } from './components/tableColumns';
import './plans-list.less';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const PlansInit = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    data,
    loading,
    tableKey,
    pagination,
    handleTableChanges,
    rowSelection,
    buildTableColumns,
    availableTableColumns,
    buildNumberLineColumn,
    buildDocumentCodeColumn,
    buildUpdatedAtColumn,
    buildDocumentInfoIconsColumn,
    buildActionsButtonColumn,
    buildColumnProps,
    query,
    tableComponents,
    hiddenColumns,
    isEditingTable,
    setIsEditingTable,
  } = useListContext();

  const storedListSettings = useZustand('module', 'listSettings');
  const storedLanguage = useZustand('theme', 'language');

  // Serialize sort, search, and filter for stable dependencies
  const sortKey = JSON.stringify(query?.sort || []);
  const searchKey = JSON.stringify(query?.search || {});
  const filterKey = JSON.stringify(query?.filter || {});

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();

  // Toolbar height tracked for fullscreen scroll max-height calculation
  const toolbarRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // Fullscreen toggle for the plans list card
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [toolbarH, setToolbarH] = useState(49);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
    appService.message({
      flag: 'i',
      text: !isFullscreen ? 'fullscreen_enabled' : 'fullscreen_disabled',
    });
  }, [isFullscreen]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Track toolbar height so fullscreen wrapper max-height fills remaining viewport
  useEffect(() => {
    const el = toolbarRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const h = entries[0].borderBoxSize?.[0]?.blockSize ?? entries[0].contentRect.height;
      setToolbarH(Math.ceil(h) + 1); // +1 for border-bottom
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Rebuild columns when sort or settings change
  useEffect(() => {
    buildTableColumns?.([
      buildNumberLineColumn(),

      buildDocumentCodeColumn(),

      planWithLogoColumn(buildColumnProps, { columnProps: { sort: true, search: false } }),

      operatorColumn(buildColumnProps),

      priceColumn(buildColumnProps),

      buildUpdatedAtColumn(),

      buildDocumentInfoIconsColumn(),

      buildActionsButtonColumn({
        view: true,
        fitInDropDownMenu: ['archive', 'remove'],
      }),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedListSettings?.showNumberOfLine,
    sortKey,
    searchKey,
    filterKey,
    storedLanguage,
    hiddenColumns,
    isEditingTable,
    query?.drawer?.folder,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  const cardStyle = isFullscreen
    ? {
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        borderRadius: 0,
        boxShadow: 'none',
        overflow: 'hidden',
        transition: 'all 0.3s ease',
      }
    : { transition: 'all 0.3s ease' };

  // Stable scroll prop — no y needed (wrapper div handles vertical scroll)
  const tableScroll = useMemo(() => ({ x: 'max-content' }), []);

  return (
    <>
      {isFullscreen && (
        <style>{`
          .ant-layout-content { z-index: 1000 !important; }
          #server-wrapper-content { z-index: 1000 !important; }
        `}</style>
      )}
      <Card
        variant="outlined"
        className="plans-list-card"
        styles={{ body: { padding: 0 } }}
        style={cardStyle}
      >
        <div className="plans-list-card__toolbar" ref={toolbarRef}>
          <div className="plans-list-card__toolbar-actions">
            <ActionsButton
              locate="pageHeader"
              affixWhen="none"
              appendButtons={
                <Tooltip title={translateX(isFullscreen ? 'restore_window' : 'expand_window')}>
                  <Button
                    type="default"
                    onClick={handleToggleFullscreen}
                    icon={isFullscreen ? <BiCollapseAlt /> : <BiExpandAlt />}
                  />
                </Tooltip>
              }
            />
          </div>
        </div>

        <div
          className={
            isFullscreen ? 'plans-list-card__table-scroll allow-inner-scroll' : undefined
          }
          style={
            isFullscreen
              ? { overflow: 'auto', maxHeight: `calc(100vh - ${toolbarH}px)` }
              : undefined
          }
        >
          <Table
            key={tableKey}
            scroll={tableScroll}
            bordered
            rowKey="_id"
            size={isMobile ? 'small' : 'large'}
            columns={availableTableColumns}
            dataSource={data || []}
            pagination={pagination}
            className="with-checkbox"
            loading={loading}
            onChange={handleTableChanges}
            rowSelection={{
              ...rowSelection,
              columnTitle: (
                <Tooltip title={translateX('customize_table')}>
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      setIsEditingTable(!isEditingTable);
                    }}
                    className="plans-list-card__expand-btn"
                  >
                    <span
                      className={`plans-list-card__edit-dot${isEditingTable ? ' active' : ''}`}
                    />
                  </span>
                </Tooltip>
              ),
              ...(isEditingTable ? { getCheckboxProps: () => ({ disabled: true }) } : {}),
            }}
            components={tableComponents}
          />
        </div>
      </Card>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PlansInit;
